import { useEffect, useState } from 'react';
import { Link } from '@/app/router';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { Button, LinkButton } from './ui';
import { cn } from '@/lib/format';
import { CloseIcon, GridIcon } from '@/lib/icons';

const LIFTER_NAV = [
  { href: '#features', label: 'Features' },
  { href: '#grid', label: 'The Grid' },
  { href: '#programme', label: 'Programme' },
  { href: '#tracks', label: 'Tracks' },
  { href: '#coach', label: 'AI Coach' },
  { href: '#pricing', label: 'Pricing' },
];

/**
 * The CRM has its own marketing pages and its own navigation — per platform
 * plan §3 the two audiences do not share a navigation bar, so this header
 * carries either the lifter sections or the CRM sections, never both.
 */
const CRM_NAV = [
  { href: '#crm-who', label: 'Who it’s for' },
  { href: '#crm-product', label: 'The product' },
  { href: '#crm-compare', label: 'Compared' },
  { href: '#crm-pricing', label: 'Pricing' },
];

export function Header({ audience = 'lifter' }: { audience?: 'lifter' | 'crm' }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isCrm = audience === 'crm';
  const nav = isCrm ? CRM_NAV : LIFTER_NAV;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 120);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full border-b border-hairline bg-page/92 backdrop-blur-md transition-[height] duration-200',
        scrolled ? 'h-[56px]' : 'h-[68px]',
      )}
    >
      <div className="mx-auto flex h-full max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] items-center justify-between gap-4 px-4 sm:px-6">
        <a href="/" data-testid="landing-header-logo" className="flex items-center gap-2 text-ink" aria-label="Lift2Fit home">
          <Logo
            variant="horizontal"
            className={cn('w-auto transition-all duration-200', scrolled ? 'h-5' : 'h-6')}
          />
        </a>

        <nav aria-label="Main" className="hidden items-center gap-7 lg:flex">
          {nav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-muted transition-colors duration-150 hover:text-accent"
            >
              {item.label}
            </a>
          ))}

          {isCrm ? (
            <a
              href="/"
              data-testid="crm-nav-lifter-app"
              className="border-l border-hairline pl-7 text-[12px] font-bold uppercase tracking-[0.08em] text-ink transition-colors duration-150 hover:text-accent"
            >
              Lifter app
            </a>
          ) : (
            <Link
              to="/for-coaches"
              data-testid="nav-for-coaches"
              className="border-l border-hairline pl-7 text-[12px] font-bold uppercase tracking-[0.08em] text-ink transition-colors duration-150 hover:text-accent"
            >
              For coaches &amp; gyms
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          {isCrm ? (
            <>
              {/* The business door. `#/crm/login`, not `#/login`: a coach
                  clicking Log in here must reach the workspace login, never the
                  lifter-branded one with its lifter demo switcher. */}
              <a
                href="#/crm/login"
                data-testid="crm-nav-login"
                className="hidden text-[12px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:text-accent sm:block"
              >
                Log in
              </a>
              {/* The CRM's front door. One orange primary per view; the Scale
                  tier's deliberate human path stays beside it, never replaced.
                  The responsive display lives on a wrapper because LinkButton's
                  own base `inline-flex` would win over a `hidden` utility. */}
              <span className="hidden sm:inline-flex">
                <LinkButton href="#/crm/signup" size="sm" data-testid="crm-nav-start-coaching">
                  Start coaching
                </LinkButton>
              </span>
              <span className="hidden lg:inline-flex">
                <LinkButton
                  href="mailto:info@lift2fit.com"
                  size="sm"
                  variant="outline"
                  data-testid="crm-nav-talk"
                >
                  Talk to us
                </LinkButton>
              </span>
            </>
          ) : (
            <>
              <a
                href="#/login"
                className="hidden text-[12px] font-bold uppercase tracking-[0.08em] text-ink transition-colors hover:text-accent sm:block"
              >
                Log in
              </a>
              <LinkButton href="#/signup" size="sm" className="hidden sm:inline-flex">
                Start free
              </LinkButton>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <CloseIcon className="text-base" /> : <GridIcon className="text-base" />}
          </Button>
        </div>
      </div>

      {menuOpen ? (
        <div id="mobile-nav" className="border-t border-hairline bg-page px-4 py-4 lg:hidden">
          <nav aria-label="Mobile" className="flex flex-col gap-1">
            {nav.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="border-b border-hairline py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-ink"
              >
                {item.label}
              </a>
            ))}
            {isCrm ? (
              <a
                href="/"
                onClick={() => setMenuOpen(false)}
                className="border-b border-hairline py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-accent"
              >
                Lifter app
              </a>
            ) : (
              <Link
                to="/for-coaches"
                data-testid="nav-for-coaches-mobile"
                onClick={() => setMenuOpen(false)}
                className="border-b border-hairline py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-accent"
              >
                For coaches &amp; gyms
              </Link>
            )}
            {isCrm ? (
              <div className="mt-3 flex flex-col gap-2">
                <a
                  href="#/crm/login"
                  onClick={() => setMenuOpen(false)}
                  data-testid="crm-nav-login-mobile"
                  className="border-b border-hairline py-3 text-[13px] font-bold uppercase tracking-[0.08em] text-ink"
                >
                  Log in
                </a>
                <LinkButton
                  href="#/crm/signup"
                  size="md"
                  data-testid="crm-nav-start-coaching-mobile"
                  onClick={() => setMenuOpen(false)}
                >
                  Start coaching
                </LinkButton>
                <LinkButton
                  href="mailto:info@lift2fit.com"
                  size="md"
                  variant="outline"
                  data-testid="crm-nav-talk-mobile"
                  onClick={() => setMenuOpen(false)}
                >
                  Talk to us
                </LinkButton>
              </div>
            ) : (
              <LinkButton href="#/signup" size="md" className="mt-3" onClick={() => setMenuOpen(false)}>
                Start free — no card
              </LinkButton>
            )}
          </nav>
        </div>
      ) : null}
    </header>
  );
}
